<template>
  <div class="bio-page py-3">
    <header class="page-toolbar">
      <div class="page-toolbar__title">
        <span class="page-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Biometric PIN → Staff</h1>
          <p>Each fingerprint on the device has a PIN. Assign the matching staff member so attendance shows real names.</p>
        </div>
      </div>
      <div class="page-toolbar__actions">
        <button type="button" class="bio-refresh" :disabled="isLoading" @click="loadAll">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" :class="{ spinning: isLoading }">
            <path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" />
          </svg>
          <span>{{ isLoading ? 'Loading…' : 'Refresh' }}</span>
        </button>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show>{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show>{{ successMessage }}</CAlert>

    <BaseTable
      title="Device PINs"
      :items="rows"
      :columns="columns"
      :loading="isLoading"
      :sortable="false"
      empty-text="No PINs seen yet — scan a finger on the device, then refresh."
    >
      <template #toolbar>
        <span>{{ mappedCount }} of {{ rows.length }} mapped</span>
      </template>
      <template #cell-first_seen="{ value }">{{ formatDate(value) }}</template>
      <template #cell-last_seen="{ value }">{{ formatDate(value) }}</template>
      <template #cell-staff="{ row }">
        <div class="bio-assign">
          <AppDropdown
            searchable
            search-placeholder="Search staff…"
            :value="row.user_id || ''"
            :options="optionsForPin(row.pin)"
            :disabled="savingPin === row.pin"
            @input="assign(row, $event)"
          />
        </div>
      </template>
      <template #cell-status="{ row }">
        <span :class="['bio-pill', row.user_id ? 'bio-pill--mapped' : 'bio-pill--unmapped']">
          {{ row.user_id ? 'Mapped' : 'Unmapped' }}
        </span>
      </template>
    </BaseTable>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import BaseTable from '@/components/BaseTable.vue'

export default {
  name: 'BiometricPinMapping',
  components: { AppDropdown, BaseTable },
  data () {
    return {
      rows: [],
      staff: [],
      isLoading: false,
      savingPin: null,
      errorMessage: '',
      successMessage: '',
      successTimer: null,
      columns: [
        { key: 'pin', label: 'PIN', width: '80px' },
        { key: 'punches', label: 'Punches', align: 'right', format: 'number' },
        { key: 'first_seen', label: 'First Seen' },
        { key: 'last_seen', label: 'Last Seen' },
        { key: 'staff', label: 'Assigned Staff', width: '320px' },
        { key: 'status', label: 'Status', width: '120px' }
      ]
    }
  },
  computed: {
    mappedCount () {
      return this.rows.filter(r => r.user_id).length
    }
  },
  created () {
    this.loadAll()
  },
  methods: {
    formatDate (iso) {
      if (!iso) return '—'
      const d = new Date(iso.replace(' ', 'T'))
      if (isNaN(d)) return iso
      return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    // Staff options for one PIN's dropdown. Flags staff already holding another
    // PIN so the admin doesn't accidentally move someone unaware.
    optionsForPin (pin) {
      const opts = [{ value: '', label: '— Unassigned —' }]
      this.staff.forEach(u => {
        let label = u.name + (u.employee_code ? ` (${u.employee_code})` : '')
        if (u.biometric_id && u.biometric_id !== pin) label += ` • now PIN ${u.biometric_id}`
        opts.push({ value: u.id, label })
      })
      return opts
    },
    loadAll () {
      this.errorMessage = ''
      this.isLoading = true
      Promise.all([
        axios.get(`${this.$apiAdress}/api/attendance/pins`),
        axios.get(`${this.$apiAdress}/api/attendance/staff`)
      ])
        .then(([pinsRes, staffRes]) => {
          this.rows = Array.isArray(pinsRes.data) ? pinsRes.data : []
          this.staff = Array.isArray(staffRes.data) ? staffRes.data : []
        })
        .catch(e => { this.errorMessage = (e.response && e.response.data && e.response.data.message) || 'Could not load PIN mappings.' })
        .finally(() => { this.isLoading = false })
    },
    assign (row, userId) {
      const newId = userId === '' ? null : Number(userId)
      if ((row.user_id || null) === newId) return
      this.savingPin = row.pin
      this.errorMessage = ''
      axios.post(`${this.$apiAdress}/api/attendance/map`, { pin: row.pin, user_id: newId })
        .then(() => {
          this.flashSuccess(newId ? `PIN ${row.pin} assigned.` : `PIN ${row.pin} unassigned.`)
          return this.loadAll()
        })
        .catch(e => { this.errorMessage = (e.response && e.response.data && e.response.data.message) || `Could not save PIN ${row.pin}.` })
        .finally(() => { this.savingPin = null })
    },
    flashSuccess (msg) {
      this.successMessage = msg
      if (this.successTimer) clearTimeout(this.successTimer)
      this.successTimer = setTimeout(() => { this.successMessage = '' }, 3000)
    }
  },
  beforeDestroy () {
    if (this.successTimer) clearTimeout(this.successTimer)
  }
}
</script>

<style scoped>
.page-toolbar { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1.5rem; gap: 1rem; }
.page-toolbar__title { display: flex; gap: 0.85rem; }
.page-toolbar__accent { display: inline-block; width: 4px; align-self: stretch; border-radius: 4px; background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%); }
.page-toolbar h1 { font-size: 1.15rem; font-weight: 600; color: #1f2a44; margin: 0; }
.page-toolbar p { font-size: 0.78rem; color: #6b7280; margin: 0.15rem 0 0; max-width: 640px; }

.bio-refresh { display: inline-flex; align-items: center; gap: 0.4rem; height: 36px; padding: 0 0.9rem; border-radius: 8px; border: 1px solid #d8dde6; background: #fff; color: #1f2a44; font-size: 0.85rem; font-weight: 600; cursor: pointer; transition: all 0.2s; }
.bio-refresh:hover { border-color: #16a07a; color: #0e5b46; }
.bio-refresh:disabled { opacity: 0.6; cursor: default; }
.bio-refresh svg { transition: transform 0.3s; }
.bio-refresh:hover svg { transform: rotate(60deg); }
.bio-refresh svg.spinning { animation: bio-spin 0.9s linear infinite; }
.bio-refresh:focus { outline: none; }
.bio-refresh:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }
@keyframes bio-spin { to { transform: rotate(360deg); } }

.bio-assign { min-width: 260px; }

.bio-pill { display: inline-block; padding: 0.22rem 0.6rem; border-radius: 999px; font-size: 0.74rem; font-weight: 600; }
.bio-pill--mapped { background: #dcfce7; color: #166534; }
.bio-pill--unmapped { background: #f3f4f6; color: #6b7280; }
</style>
